import DataRoomAppBar from "@/components/data-room/DataRoomAppBar";
import { ALL_ENTRIES_JOINED, MASTER_ENTRY, findEntryByStaticId } from "@/components/data-room/DataRoomCategoriesDefines";
import DataRoomDirectoryFileTree, { TREE_FILTERS } from "@/components/data-room/DataRoomDirectoryFileTree";
import DataRoomManageSection from "@/components/data-room/DataRoomManageSection";
import DataRoomRedFlagsSection from "@/components/data-room/DataRoomRedFlagsSection";
import { DataRoomProvider, useDataRoomProvider } from "@/components/data-room/contexts/DataRoomContext";
import { useTenantMemberships } from "@/contexts/TenantContext";
import { Colors } from "@/enums/Colors";
import { useResponsive } from "@/hooks/useResponsive";
import { DataRoomFile } from "@/models/data-room/DataRoomFile";
import DataRoomCustomFolderService from "@/services/DataRoomCustomFolderService";
import DataRoomService from "@/services/DataRoomService";
import { Close as CloseIcon, Menu as MenuIcon } from "@mui/icons-material";
import { Box, Fab, styled } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { DataRoomEntry } from "./OneDataRoomPage";

export enum DataRoomFileSection {
  MANAGE,
  RED_FLAGS,
}

type LocationState = {
  section: DataRoomFileSection;
  entry?: DataRoomEntry;
};

function OneDataRoomFilesPageContent() {
  const [loading, setLoading] = useState<boolean>(true);
  const [files, setFiles] = useState<DataRoomFile[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<DataRoomFile[]>([]);
  const [showMobileTree, setShowMobileTree] = useState<boolean>(true);

  const location = useLocation();
  const locState = location.state as LocationState;
  const navigationEntry = locState?.entry || MASTER_ENTRY;
  const { isMobile, isTablet } = useResponsive();
  const isMobileOrTablet = isMobile || isTablet;

  const {
    selected: selectedEntry,
    setSelected: setSelectedEntry,
    selectedCustom,
    section,
    setSection,
    treeFilter,
    setTreeFilter,
  } = useDataRoomProvider();

  const [tree, setTree] = useState<DataRoomEntry>({ ...ALL_ENTRIES_JOINED });

  const [searchParams] = useSearchParams();
  const documentId = Number(searchParams.get("documentId"));

  const [focusedFile, setFocusedFile] = useState<DataRoomFile>(null);

  const { currentTenantMembership } = useTenantMemberships();

  useEffect(() => setSelectedEntry(navigationEntry), [navigationEntry]);

  useEffect(() => {
    if (!documentId || !currentTenantMembership.tenant.externalId) return;
    DataRoomService.getByDocumentId(documentId, currentTenantMembership.tenant.externalId).then((r) => {
      r.onSuccess((c) => {
        onClickSearchOpt(c);
      });
    });
  }, [documentId, currentTenantMembership.tenant.externalId]);

  function fetchFiles() {
    setLoading(true);

    if (treeFilter === TREE_FILTERS.CUSTOM && selectedCustom) {
      DataRoomCustomFolderService.getFiles(selectedCustom.externalId)
        .then((r) => r.onSuccess((c) => setFiles(c)).notifyErrorIfPresent())
        .finally(() => setLoading(false));
    } else {
      DataRoomService.getFiles(selectedEntry?.staticId, currentTenantMembership.tenant.externalId)
        .then((r) => r.onSuccess((c) => setFiles(c)).notifyErrorIfPresent())
        .finally(() => setLoading(false));
    }
  }

  useEffect(() => setSelectedEntry(MASTER_ENTRY), [currentTenantMembership.tenant.externalId]);
  useEffect(() => fetchFiles(), [selectedEntry, selectedCustom, treeFilter]);

  const SECTIONS_COMPONENTS = {
    [DataRoomFileSection.MANAGE]: (
      <DataRoomManageSection
        setFiles={setFiles}
        setSelectedFiles={setSelectedFiles}
        selectedFiles={selectedFiles}
        files={files}
        loading={loading}
        setTree={setTree}
        focusedFile={focusedFile}
        setFocusedFile={setFocusedFile}
      />
    ),
    [DataRoomFileSection.RED_FLAGS]: <DataRoomRedFlagsSection />,
  };

  const onClickSearchOpt = useCallback(
    (opt: DataRoomEntry | DataRoomFile) => {
      let entry: DataRoomEntry | DataRoomFile = null;
      if ("parentStaticId" in opt) {
        entry = findEntryByStaticId(opt.parentStaticId);
        setFocusedFile(opt);
      } else {
        entry = opt;
      }
      setSelectedEntry(entry);
      if (treeFilter !== TREE_FILTERS.ACTIVE && treeFilter !== TREE_FILTERS.COMMON) {
        setTreeFilter(TREE_FILTERS.COMMON);
      }
    },
    [treeFilter]
  );

  return (
    <PageRoot>
      <DataRoomAppBar onClickMenuOpt={onClickSearchOpt} makeStatic section={section} setSection={setSection} />
      <BodyContainer isMobileOrTablet={isMobileOrTablet}>
        {isMobileOrTablet ? (
          // Mobile/Tablet Layout
          <>
            {showMobileTree ? (
              <MobileTreeContainer>
                <DataRoomDirectoryFileTree
                  hoverColor={Colors.BUTTON_SECONDARY_HOVER_PURPLE}
                  tree={tree}
                  setTree={setTree}
                  onNodeClick={() => setShowMobileTree(false)}
                />
              </MobileTreeContainer>
            ) : (
              <MobileContentContainer>{SECTIONS_COMPONENTS[section]}</MobileContentContainer>
            )}
            <MobileToggleFab
              color="primary"
              onClick={() => setShowMobileTree(!showMobileTree)}
              sx={{ position: "fixed", bottom: 64, right: 16 }}
            >
              {showMobileTree ? <CloseIcon /> : <MenuIcon />}
            </MobileToggleFab>
          </>
        ) : (
          // Desktop Layout
          <>
            <LeftContainer>
              <DataRoomDirectoryFileTree hoverColor={Colors.BUTTON_SECONDARY_HOVER_PURPLE} tree={tree} setTree={setTree} />
            </LeftContainer>
            <RightContainer>{SECTIONS_COMPONENTS[section]}</RightContainer>
          </>
        )}
      </BodyContainer>
    </PageRoot>
  );
}

const PageRoot = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "100vh",
  overflow: "hidden",
});

const BodyContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMobileOrTablet",
})<{ isMobileOrTablet?: boolean }>(({ isMobileOrTablet }) => ({
  display: "flex",
  width: "100%",
  flex: 1,
  minHeight: 0,
  position: "relative",
  ...(isMobileOrTablet && {
    flexDirection: "column",
  }),
}));

const LeftContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  overflow: "auto",
  height: "100%",
  minWidth: "234px",
  flex: "1",
  borderRight: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
});

const RightContainer = styled(Box)({
  backgroundColor: Colors.WHITE,
  gap: "1rem",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  alignItems: "center",
  justifyContent: "flex-start",
  flex: "4",
});

const MobileTreeContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  overflow: "hidden",
  backgroundColor: Colors.WHITE,
}));

const MobileContentContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  overflow: "auto",
  backgroundColor: Colors.WHITE,
}));

const MobileToggleFab = styled(Fab)(({ theme }) => ({
  backgroundColor: Colors.LAZO_PURPLE,
  color: Colors.WHITE,
  "&:hover": {
    backgroundColor: Colors.BUTTON_HOVER_PURPLE,
  },
  zIndex: theme.zIndex.speedDial,
}));

export default function OneDataRoomFilesPage() {
  return (
    <DataRoomProvider>
      <OneDataRoomFilesPageContent />
    </DataRoomProvider>
  );
}
